import React, { useState } from "react";
import { useContext } from "react";
import { LoginContext } from "context/Auth/LoginContext";
import { useTranslation } from "react-i18next";
import { Lock, Eye, EyeSlash, CheckCircle, ShieldCheck } from "@phosphor-icons/react";
import usePost from "Hooks/usePost";
import Spinner from "Components/RequestHandler/Spinner";
import { StatusHandler } from "Components/ApiNotification";

const ChangePassword = () => {
  const { token } = useContext(LoginContext);
  const { t } = useTranslation();
  const { postData, isLoading, isError, data } = usePost();

  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  const [errors, setErrors] = useState({});
  const [changeSuccess, setChangeSuccess] = useState(false);

  const validatePassword = (password) => {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
      password
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setChangeSuccess(false);
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!formData.oldPassword) {
      newErrors.oldPassword = t("account.oldPasswordError");
    }

    if (!formData.newPassword || !validatePassword(formData.newPassword)) {
      newErrors.newPassword = t("account.newPasswordError");
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t("account.confirmPasswordError");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const response = await postData("auth/change-password", formData, token);

    if (response?.success) {
      setChangeSuccess(true);
      setFormData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  };

  const PasswordField = ({ name, label, showKey }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Lock size={20} className="text-gray-400" />
        </div>
        <input
          type={showPasswords[showKey] ? "text" : "password"}
          name={name}
          value={formData[name]}
          onChange={handleChange}
          className={`w-full pl-10 pr-12 py-3 border rounded-lg transition-colors ${
            errors[name]
              ? "border-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-500"
              : "border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary"
          }`}
          placeholder="••••••••"
        />
        <button
          type="button"
          onClick={() => togglePasswordVisibility(showKey)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
        >
          {showPasswords[showKey] ? <EyeSlash size={20} /> : <Eye size={20} />}
        </button>
      </div>
      {errors[name] && (
        <p className="mt-1 text-sm text-red-500">{errors[name]}</p>
      )}
    </div>
  );

  return (
    <div className="font-text">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <ShieldCheck size={24} className="text-primary" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">
            {t("account.changePassword")}
          </h2>
          <p className="text-sm text-gray-500">
            {t("account.changePasswordDesc") || "Keep your account secure with a strong password"}
          </p>
        </div>
      </div>

      <StatusHandler error={isError} data={data} />

      {changeSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <CheckCircle size={20} weight="fill" />
          <span>{t("account.passwordChanged") || "Password changed successfully!"}</span>
        </div>
      )}

      {/* Password Requirements */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
        <p className="text-sm font-medium text-blue-800 mb-2">
          {t("account.passwordRequirements") || "Password Requirements:"}
        </p>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• {t("account.passwordReq1") || "At least 8 characters"}</li>
          <li>• {t("account.passwordReq2") || "One uppercase letter"}</li>
          <li>• {t("account.passwordReq3") || "One lowercase letter"}</li>
          <li>• {t("account.passwordReq4") || "One number"}</li>
          <li>• {t("account.passwordReq5") || "One special character (@$!%*?&)"}</li>
        </ul>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <PasswordField
          name="oldPassword"
          label={t("common.oldPassword")}
          showKey="old"
        />

        <PasswordField
          name="newPassword"
          label={t("common.newPassword")}
          showKey="new"
        />

        <PasswordField
          name="confirmPassword"
          label={t("common.confirmPassword")}
          showKey="confirm"
        />

        <div className="pt-4">
          <button
            type="submit"
            disabled={isLoading}
            className="px-6 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primaryDark transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? <Spinner isSmall /> : t("account.updatePassword")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangePassword;
